/**
 * Escuta pelo navegador (webkitSpeechRecognition).
 *
 * É o plano B enquanto não há chave da OpenAI, e o plano B permanente para
 * quando o Realtime cai no meio do dia. O reconhecedor do navegador é grátis e
 * está sempre lá, mas não tem VAD semântico, não tem barge-in e devolve a
 * própria voz do assistente como se fosse do usuário. As três coisas que
 * faltam estão implementadas aqui à mão: barge-in, guarda de eco e fim de
 * turno com espera variável.
 */

import { DEFAULT_LANGUAGE } from './config';
import { abrirMicrofone, criarMedidorDeNivel, fecharStream } from './mic';
import type { MedidorDeNivel } from './mic';
import { ehEco, esperaFimDeTurno } from './turno';
import type {
  HeardHandler,
  LevelHandler,
  Listener,
  SpeechRecognitionCtor,
  SpeechRecognitionLike,
  WindowComVoz,
} from './types';

export interface BrowserListenerOptions {
  language?: string;
  onError?: (erro: Error) => void;
}

function construtorDeReconhecimento(): SpeechRecognitionCtor | null {
  const w = window as unknown as WindowComVoz;
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function createBrowserListener(opts: BrowserListenerOptions = {}): Listener {
  const idioma = opts.language ?? DEFAULT_LANGUAGE;

  const ouvintesTexto: HeardHandler[] = [];
  const ouvintesNivel: LevelHandler[] = [];
  const ouvintesBargeIn: Array<() => void> = [];

  let rec: SpeechRecognitionLike | null = null;
  let stream: MediaStream | null = null;
  let medidor: MedidorDeNivel | null = null;
  let rodando = false;

  /** O que o Will está dizendo agora — `null` quando ele está calado. */
  let textoFalado: string | null = null;

  /** Trechos que o reconhecedor já deu por finais dentro do turno corrente. */
  let fechados: string[] = [];
  let parcial = '';
  let relogio: ReturnType<typeof setTimeout> | null = null;

  const textoDoTurno = (): string => [...fechados, parcial].join(' ').replace(/\s+/g, ' ').trim();

  const emitir = (final: boolean): void => {
    const texto = textoDoTurno();
    if (!texto) return;
    for (const cb of ouvintesTexto) cb(texto, final);
  };

  const fecharTurno = (): void => {
    relogio = null;
    if (!textoDoTurno()) return;
    emitir(true);
    fechados = [];
    parcial = '';
  };

  /**
   * O reconhecedor do navegador marca `isFinal` cedo demais — ele fecha por
   * pausa acústica curta, no meio de uma frase que continua. O turno de
   * verdade é este relógio: ele reinicia a cada palavra nova e só fecha
   * quando o silêncio dura o suficiente para a última palavra dita.
   */
  const reagendarFimDeTurno = (): void => {
    if (relogio !== null) clearTimeout(relogio);
    relogio = setTimeout(fecharTurno, esperaFimDeTurno(textoDoTurno()));
  };

  const dispararBargeIn = (): void => {
    for (const cb of ouvintesBargeIn) cb();
  };

  const aoResultado = (ev: { resultIndex: number; results: SpeechRecognitionResultList }): void => {
    const novosFinais: string[] = [];
    let interino = '';

    for (let i = ev.resultIndex; i < ev.results.length; i++) {
      const resultado = ev.results[i];
      const trecho = resultado[0].transcript;
      if (resultado.isFinal) novosFinais.push(trecho);
      else interino += trecho;
    }

    const bruto = [...novosFinais, interino].join(' ').trim();
    if (!bruto) return;

    if (textoFalado !== null) {
      /**
       * Guarda de eco. O alto-falante toca a voz do Will, o microfone a
       * captura e o reconhecedor a transcreve com toda a boa vontade. Se o
       * que chegou é pedaço do que está sendo dito agora, é a própria voz
       * voltando — descartar aqui é o que impede o Will de se interromper
       * sozinho a cada frase.
       */
      if (ehEco(bruto, textoFalado)) return;

      // Passou pela guarda com o Will falando: é a pessoa por cima. Cortar a
      // fala agora, antes de qualquer processamento, é o que faz a interrupção
      // parecer natural em vez de teimosa.
      dispararBargeIn();
    }

    for (const t of novosFinais) {
      const limpo = t.trim();
      if (limpo) fechados.push(limpo);
    }
    parcial = interino.trim();

    emitir(false);
    reagendarFimDeTurno();
  };

  const reiniciar = (): void => {
    if (!rodando || !rec) return;
    // O Chrome encerra a sessão sozinho depois de um tempo de silêncio. Sem
    // este religamento a escuta morre em silêncio, sem erro nenhum.
    setTimeout(() => {
      if (!rodando || !rec) return;
      try {
        rec.start();
      } catch {
        // start() com a sessão ainda ativa lança InvalidStateError; o próximo
        // onend traz outra chance.
      }
    }, 120);
  };

  const start = async (): Promise<void> => {
    if (rodando) return;

    const Ctor = construtorDeReconhecimento();
    if (!Ctor) {
      throw new Error(
        'Este navegador não tem reconhecimento de fala. Use o Chrome ou fale por texto.',
      );
    }

    /**
     * O reconhecedor abre a própria captura e não expõe constraints. Abrimos
     * o stream mesmo assim por dois motivos: o medidor de nível precisa dele,
     * e a permissão concedida com cancelamento de eco ativo é o que o Chrome
     * aplica ao dispositivo. Como isso não é garantia, a guarda de eco
     * continua sendo necessária.
     */
    stream = await abrirMicrofone();
    medidor = criarMedidorDeNivel(stream, (nivel) => {
      for (const cb of ouvintesNivel) cb(nivel);
    });

    rec = new Ctor();
    rec.lang = idioma;
    rec.continuous = true;
    rec.interimResults = true; // sem parciais não há barge-in nem rascunho na tela
    rec.maxAlternatives = 1;
    rec.onresult = aoResultado;
    rec.onend = reiniciar;
    rec.onerror = (ev): void => {
      if (ev.error === 'not-allowed' || ev.error === 'service-not-allowed') {
        rodando = false;
        opts.onError?.(new Error('Permissão de microfone negada.'));
        return;
      }
      // 'no-speech', 'aborted' e 'network' são rotina numa escuta contínua:
      // o onend subsequente religa.
    };

    rodando = true;
    rec.start();
  };

  const stop = (): void => {
    rodando = false;
    if (relogio !== null) {
      clearTimeout(relogio);
      relogio = null;
    }
    if (rec) {
      rec.onend = null;
      rec.onresult = null;
      rec.onerror = null;
      try {
        rec.abort();
      } catch {
        // abortar uma sessão já encerrada não é problema
      }
      rec = null;
    }
    medidor?.parar();
    medidor = null;
    fecharStream(stream);
    stream = null;
    fechados = [];
    parcial = '';
    textoFalado = null;
  };

  return {
    start,
    stop,
    onHeard: (cb) => void ouvintesTexto.push(cb),
    onLevel: (cb) => void ouvintesNivel.push(cb),
    onBargeIn: (cb) => void ouvintesBargeIn.push(cb),
    setTalking: (talking, text) => {
      textoFalado = talking ? (text ?? '') : null;
    },
  };
}
